// Composer deep links (ENG-5231): `/new?skill=<slug>&prompt=<text>` lands the
// user on a composer already holding the use case an ad group or a marketing
// sample promised, with the skill's slash trigger in front when the slug names
// a skill in the live catalog. Nothing auto-submits: the user still presses
// send, so a bad link never burns a turn.
//
// Why a stash: the New Task pane resolves `/new` to a scratch task and
// replaces the URL with `/task/<id>` (tasks.tsx `loadByRoute`), and a
// signed-out arrival is bounced to `/login?next=/new?...` and, for an
// email/password signup, through the verification email, which returns to a
// bare `/new`. So the link is captured on FIRST PAINT of any page (Providers
// mount, reading the URL or its same-origin `?next=` target), kept for the
// same two hours the signup intent lives, and consumed once a task is ready
// to be seeded. The URL, when it still carries the params, always wins.

"use client";

import { listSkills } from "@/lib/api";
import { scrubUntrustedValue } from "./signup-funnel";
import { seedTaskDraft } from "./task-seed";

export const DEEP_LINK_SOURCE = "deep_link";
const STASH_KEY = "da_composer_deep_link";
const MAX_AGE_MS = 2 * 60 * 60 * 1000; // matches the signup intent's verify hop
export const MAX_PROMPT_LENGTH = 2000;
const COMPOSER_PATHS = new Set(["/new", "/"]);
// A skill slug as the catalog names it (`cim`, `ic-memo`, `buyer_list`, …).
const SKILL_SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export type ComposerDeepLink = { skill: string | null; prompt: string };

function cleanPrompt(raw: string | null): string {
  if (!raw) return "";
  // `URLSearchParams` already decoded the value once; the user sees this
  // text verbatim, so a literal `%2F` in it must not be decoded again.
  return scrubUntrustedValue(raw, MAX_PROMPT_LENGTH, { decode: false }).trim();
}

/** The link named by `params`, or null when there is nothing to seed. A
 * prompt is scrubbed like every other URL-borne value and capped; a skill
 * that is not slug-shaped is dropped (the prompt alone still seeds). */
export function parseComposerDeepLink(params: URLSearchParams): ComposerDeepLink | null {
  const rawSkill = (params.get("skill") ?? "").trim();
  const skill = SKILL_SLUG_RE.test(rawSkill) ? rawSkill : null;
  const prompt = cleanPrompt(params.get("prompt"));
  if (!skill && !prompt) return null;
  return { skill, prompt };
}

/** The params to read a link from: the page's own when it is a composer
 * route, else a same-origin `?next=` target that is one (the login bounce).
 * Cross-origin targets are never input. */
export function composerDeepLinkParams(location: {
  pathname: string;
  search: string;
  origin: string;
}): URLSearchParams | null {
  const params = new URLSearchParams(location.search);
  if (COMPOSER_PATHS.has(location.pathname)) return params;
  const next = params.get("next");
  if (!next) return null;
  try {
    const target = new URL(next, location.origin);
    if (target.origin !== location.origin || !COMPOSER_PATHS.has(target.pathname)) return null;
    return target.searchParams;
  } catch {
    return null;
  }
}

function loadStash(): ComposerDeepLink | null {
  try {
    const raw = window.localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as { link?: unknown; ts?: unknown } | null;
    const now = Date.now();
    if (
      !value ||
      typeof value !== "object" ||
      typeof value.ts !== "number" ||
      !Number.isFinite(value.ts) ||
      value.ts > now ||
      now - value.ts > MAX_AGE_MS ||
      !value.link ||
      typeof value.link !== "object"
    ) {
      clearComposerDeepLink();
      return null;
    }
    const stored = value.link as { skill?: unknown; prompt?: unknown };
    const params = new URLSearchParams();
    if (typeof stored.skill === "string") params.set("skill", stored.skill);
    if (typeof stored.prompt === "string") params.set("prompt", stored.prompt);
    const link = parseComposerDeepLink(params);
    if (!link) clearComposerDeepLink();
    return link;
  } catch {
    return null;
  }
}

/** Stash the link on the landing URL (or its `?next=` target). Call once per
 * page load; a page carrying no link leaves an existing stash alone. */
export function rememberComposerDeepLink(): void {
  if (typeof window === "undefined") return;
  const params = composerDeepLinkParams(window.location);
  const link = params ? parseComposerDeepLink(params) : null;
  if (!link) return;
  try {
    window.localStorage.setItem(STASH_KEY, JSON.stringify({ link, ts: Date.now() }));
  } catch {
    /* storage disabled — the URL may still carry the link when the pane loads */
  }
}

export function clearComposerDeepLink(): void {
  try {
    window.localStorage.removeItem(STASH_KEY);
  } catch {
    /* ignore */
  }
}

/** The link to apply now: the current URL's own when it carries one, else
 * the stash. Non-consuming. */
export function readComposerDeepLink(): ComposerDeepLink | null {
  if (typeof window === "undefined") return null;
  const params = composerDeepLinkParams(window.location);
  return (params && parseComposerDeepLink(params)) ?? loadStash();
}

/** `/<slug> <prompt>` when the slug names a live skill, else the prompt alone
 * (an unknown slug is dropped rather than sent as a bogus slash command). */
export function composerSeedText(link: ComposerDeepLink, knownSkills: Iterable<string>): string {
  if (link.skill) {
    const wanted = link.skill.toLowerCase();
    for (const name of knownSkills) {
      if (name.toLowerCase() === wanted) {
        return link.prompt ? `/${name} ${link.prompt}` : `/${name}`;
      }
    }
  }
  return link.prompt;
}

/**
 * Seed `taskId`'s composer from the pending link, if any. Resolves the skill
 * against the live catalog (one request, only when a slug is present; a
 * failed fetch treats it as unknown), never auto-submits, and labels the
 * first send `deep_link` for analytics. Returns the seeded draft text, or
 * null when nothing was seeded, so a caller whose composer is already
 * mounted on that task can update the live draft too (the draft hook only
 * re-reads storage when its key changes).
 */
export async function applyComposerDeepLink(
  taskId: string,
  fetchSkills: () => Promise<Array<{ name: string }>> = listSkills,
): Promise<string | null> {
  const link = readComposerDeepLink();
  if (!link) return null;
  let known: string[] = [];
  if (link.skill) {
    try {
      known = (await fetchSkills()).map((s) => s.name);
    } catch {
      known = [];
    }
  }
  const text = composerSeedText(link, known);
  clearComposerDeepLink();
  if (!text) return null;
  seedTaskDraft(taskId, text, [], { autoSubmit: false, source: DEEP_LINK_SOURCE });
  return text;
}
